/**
 * Audio cues for continuous barcode scanning.
 *
 * A single shared AudioContext is reused across scans (creating one per scan
 * leaks contexts and hits the browser's ~6-context limit at volume). Mobile
 * Safari/Chrome gate Web Audio behind a user gesture, so `unlockScanAudio()`
 * must be called from the tap that opens the camera; otherwise the first cue
 * is silent.
 */

let sharedCtx: AudioContext | null = null

function getCtx(): AudioContext | null {
    if (typeof window === 'undefined') return null
    try {
        if (!sharedCtx) {
            sharedCtx = new AudioContext()
        }
        return sharedCtx
    } catch {
        return null
    }
}

/** Resume the shared context on a user gesture (mobile audio unlock). */
export function unlockScanAudio(): void {
    const ctx = getCtx()
    if (ctx && ctx.state === 'suspended') {
        void ctx.resume()
    }
}

function tone(
    ctx: AudioContext,
    freq: number,
    start: number,
    duration: number,
    type: OscillatorType,
    peak: number,
): void {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.frequency.value = freq
    osc.type = type
    gain.gain.setValueAtTime(peak, ctx.currentTime + start)
    gain.gain.exponentialRampToValueAtTime(
        0.01,
        ctx.currentTime + start + duration,
    )
    osc.start(ctx.currentTime + start)
    osc.stop(ctx.currentTime + start + duration)
}

/** Success "ting": short two-tone rising sine — the per-package accept signal. */
export function playScanSuccess(): void {
    const ctx = getCtx()
    if (!ctx) return
    try {
        if (ctx.state === 'suspended') void ctx.resume()
        tone(ctx, 880, 0, 0.12, 'sine', 0.3)
        tone(ctx, 1320, 0.13, 0.18, 'sine', 0.3)
    } catch {
        /* Web Audio not supported — silent fallback. */
    }
}

/** Failure "buzz": a low square tone, clearly distinct from the success ting. */
export function playScanError(): void {
    const ctx = getCtx()
    if (!ctx) return
    try {
        if (ctx.state === 'suspended') void ctx.resume()
        tone(ctx, 200, 0, 0.35, 'square', 0.25)
    } catch {
        /* Web Audio not supported — silent fallback. */
    }
}
